"use client";

import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Asterisk, Crown, MoreHorizontal, Search, StickyNote, TriangleAlert, Users, Star } from "lucide-react";

import { ProfilePreviewTrigger } from "@/features/profile-preview/ui/profile-preview-trigger";
import { TwitchStatusIcon } from "@/features/stream/twitch";
import { useTournamentStore } from "@/entities/tournament";
import { toast } from "sonner";

import { api } from "@/shared/lib/api";
import { useDraftShortlist } from "@/shared/lib/hooks/use-draft-shortlist";
import { checkDraftFeasibility } from "@/widgets/tournament-management/draft-feasibility";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { cn } from "@/shared/lib/utils";
import { getOpggUrl } from "@/shared/lib/opgg";
import { withDiscordAvatarSize } from "@/shared/lib/image-utils";
import { getRiotRankImageUrl } from "@/shared/lib/rank-image";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { ChampionIcon } from "@/shared/ui/data-display/champion-icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { Input } from "@/shared/ui/inputs/input";
import { Badge } from "@/shared/ui/data-display/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/overlays/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import { tournamentKeys } from "@/shared/api/query-keys";
import {
  displayRank,
  rankTierName,
  rankWeight,
} from "@/shared/lib/lol-rank";
import {
  useTournamentStreams,
  type PlayerStreamData,
} from "@/entities/tournament/api/use-tournament-streams";
import { useBreakpoint } from "@/shared/lib/hooks/use-media-query";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";

export interface TournamentParticipant {
  id: string;
  userId: string;
  userDisplayName: string;
  userAvatarUrl?: string;
  riotAccountGameName: string;
  riotAccountTagLine: string;
  /** Регион аккаунта — op.gg-ссылка должна вести на «свой» регион (RU/EUW1/…). */
  riotAccountRegion?: string;
  /** Пусто = «любая роль»: игрок готов на любую линию. */
  mainRole?: string;
  secondaryRole?: string;
  /** Participant | Captain — пожелание игрока быть капитаном. */
  desiredRole: string;
  /** Заметка игрока о себе — показывается по наведению на иконку. */
  playerNote?: string;
  points: number;
  moderatorAdjustedPoints?: number;
  status: string;
  registeredAt: string;
  /** Бэкенд отдаёт ранг как currentRank (TournamentParticipantDto.CurrentRank).
   *  Старый код читал несуществующий `rank` — ранги на странице не отображались вовсе. */
  currentRank?: string;
  /** Ранг аккаунта, по которому РЕАЛЬНО считали цену: цена берёт максимум по всем
   *  аккаунтам игрока, а currentRank — это аккаунт, с которого регистрировались. */
  evaluatedRank?: string;
  /** Регион того аккаунта. Показываем рядом, когда он не совпадает с регистрационным,
   *  иначе «MASTER I» у игрока без ранга на RU читается как ошибка. */
  evaluatedRankRegion?: string;
  topChampionMasteries?: {
    championId: number;
    championName: string;
    championLevel: number;
    championPoints: number;
    lastPlayTime: string;
  }[];
  /**
   * Назначен капитаном слота драфта (TournamentDraftCaptains) — бэкенд отдаёт
   * это напрямую в GetParticipantsAsync, поэтому пометка видна и ДО старта
   * драфта (слоты назначаются организатором заранее, DraftTeam ещё нет).
   */
  isAppointedCaptain?: boolean;
}

interface TournamentParticipantsGridProps {
  /** Бюджет команды (включая цену капитана) — для проверки, соберутся ли составы. */
  captainBudget?: number;
  /** Ссылка на настройки драфта — куда ведёт кнопка «Поправить». */
  draftSettingsHref?: string;
  /**
   * Драфт завершён — только тогда показываем корону НАЗНАЧЕННЫМ капитанам.
   * До этого капитаны лежат за авторизацией (/draft/captains даёт 401 анониму),
   * а «хочет быть капитаном» короны не заслуживает: он ещё никем не назначен.
   */
  draftCompleted?: boolean;
  tournamentId: string;
  /** Сколько команд соберут капитаны — для подсказки «нужно N» в плитках ролей. */
  teamsNeeded?: number;
  /** Организатор/стафф: модерация живёт прямо в списке (бейдж + действия в строке). */
  isManager?: boolean;
  /** Капитан драфта: колонка-звёздочка и фильтр «Избранные» (личный шортлист). */
  isCaptain?: boolean;
  /**
   * Готовый снапшот участников — для страницы архива. Живая вкладка турнира тянет
   * список из /tournaments/{id}/participants, а тот отдаёт ТЕКУЩИЙ ранг игрока
   * (не тот, что был на момент турнира). На проде это проверено не гипотетически:
   * спустя двое суток после турнира 19 из 53 участников уже разошлись с рангом,
   * по которому их оценивали (MASTER I → GRANDMASTER I, CHALLENGER I → MASTER I,
   * один — MASTER I → Unranked после декея). Архиву нужна ЗАФИКСИРОВАННАЯ картина
   * турнира, а не то, что стало с игроками сегодня — поэтому список передаётся
   * готовым пропом вместо похода за живыми данными.
   *
   * Когда проп передан, компонент не делает вообще никаких сетевых запросов за
   * участниками (ни публичный /participants, ни менеджерский /registrations) —
   * группировка, фильтры, сортировки и плитки ролей работают на переданных данных.
   */
  participants?: TournamentParticipant[];
}

const roleOrder = ["Top", "Jungle", "Mid", "Adc", "Support"] as const;
type RoleKey = (typeof roleOrder)[number];

/** Ранг участника: бэкенд шлёт currentRank; `rank` оставлен как фолбэк. */
/**
 * Ранг для показа и группировки: текущий, а без него — тот, по которому оценивали.
 * Правила разбора вынесены в shared/lib/lol-rank, чтобы их можно было проверить тестами.
 */
function rankOf(p: TournamentParticipant): string | undefined {
  return displayRank(p.currentRank ?? (p as { rank?: string }).rank, p.evaluatedRank);
}

/** Стоимость игрока: ручная корректировка модератора важнее расчётной. */
function costOf(p: TournamentParticipant): number {
  const raw = p.moderatorAdjustedPoints ?? p.points ?? 0;
  return Number.isFinite(raw) ? raw : 0;
}

function isKnownRole(role?: string): role is RoleKey {
  return !!role && (roleOrder as readonly string[]).includes(role);
}

/**
 * Назначен ли игрок капитаном: слот из TournamentDraftCaptains (до старта драфта)
 * ИЛИ уже captainId собранной команды (после драфта). Та же логика, что рисует
 * бейдж «Капитан» у строки/карточки — фильтр должен показывать ровно тех, у кого
 * бейдж виден, а не только «сырое» поле с бэкенда.
 */
function isAppointedCaptainOf(
  p: TournamentParticipant,
  captainUserIds: ReadonlySet<string>,
): boolean {
  return Boolean(p.isAppointedCaptain) || captainUserIds.has(p.userId);
}

/** Грубый тир ранга для фильтра. Считаем по названию, а не по очкам:
 *  очки настраиваются организатором и у разных турниров разная шкала. */
function rankTier(rank?: string): "high" | "mid" | "low" {
  const r = (rank ?? "").toLowerCase();
  if (r.includes("challenger") || r.includes("grandmaster") || r.includes("master")) return "high";
  if (r.includes("diamond") || r.includes("emerald")) return "mid";
  return "low";
}

/** Стабильная пустая ссылка: `= []` в деструктуризации ломал бы мемоизацию ниже. */
const EMPTY_PARTICIPANTS: TournamentParticipant[] = [];

export function TournamentParticipantsGrid({
  tournamentId,
  teamsNeeded,
  isManager = false,
  isCaptain = false,
  draftCompleted = false,
  captainBudget,
  draftSettingsHref,
  participants: fixedParticipants,
}: TournamentParticipantsGridProps) {
  const tCommon = useTranslations("Common");
  const tUi = useTranslations("Ui");
  const t = useTranslations("Tournaments");
  // Таблица (lg+) и карточки (мобильные) раньше рендерились ОБЕ, CSS прятал одну:
  // на драфте из 13 команд это ~1600 невидимых узлов, которые React создавал и
  // гидратировал. Сетка клиентская и в SSR её нет — расхождения гидратации не будет.
  const isDesktop = useBreakpoint("lg").matches;
  const queryClient = useQueryClient();
  const refreshTournamentFromStore = useTournamentStore((s) => s.fetchTournamentById);
  /**
   * Ключ включает isManager: организатору в список подмешиваются неподтверждённые
   * заявки, поэтому менеджерский и обычный ответы нельзя держать в одной ячейке кэша.
   * Префикс ["tournaments", id] совпадает с тем, что инвалидирует
   * refreshAfterRegistrationChange — после регистрации/отмены список обновится сам.
   */
  const participantsQueryKey = useMemo(
    () => ["tournaments", tournamentId, "participants", isManager] as const,
    [tournamentId, isManager],
  );

  // Фильтры/поиск/сортировка — состояние только клиентское. Переключатель
  // плотности («Обычный/Плотный») убран по просьбе владельца — боевая
  // страница уже жила в компактном режиме, вторая плотность не использовалась.
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | RoleKey | "none">("all");
  const [rankFilter, setRankFilter] = useState<"all" | "high" | "mid" | "low">("all");
  const [costFilter, setCostFilter] = useState<"all" | "top" | "mid" | "low">("all");
  const [sort, setSort] = useState<"cost" | "cost-asc" | "nick" | "date">("cost");
  const [onlyPending, setOnlyPending] = useState(false);
  const [onlyShortlist, setOnlyShortlist] = useState(false);
  const [onlyCaptains, setOnlyCaptains] = useState(false);
  const { shortlist, toggleShortlist } = useDraftShortlist(tournamentId, isCaptain);
  const [moderatingId, setModeratingId] = useState<string | null>(null);
  // Действия из меню строки подтверждаются модалками (как в макете): смена роли —
  // отдельный диалог с выбором позиции, исключение и подтверждение заявки — alert-диалоги.
  const [roleDialog, setRoleDialog] = useState<TournamentParticipant | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<
    { participant: TournamentParticipant; action: "approve" | "reject" | "blacklist" } | null
  >(null);

  /**
   * Список участников. Раньше это был useState+useEffect с прямым api.get, и вкладка
   * рендерится условно (`activeTab === "teams"`) — то есть при каждом уходе с вкладки
   * компонент размонтировался, а при возврате грузил всё заново, с нуля и со скелетоном.
   *
   * React Query держит данные в кэше между размонтированиями: возврат на вкладку
   * показывает список мгновенно. `refetchOnMount: true` переопределяет глобальный
   * `false` — при устаревших данных фон тихо обновит их, не пряча уже показанное
   * (isLoading остаётся false, пока в кэше что-то есть).
   */
  const fetchParticipants = useCallback(async (): Promise<TournamentParticipant[]> => {
    // Публичный список — только одобренные. Организатору дополнительно подмешиваем
    // неподтверждённые заявки из менеджерского эндпоинта: модерация живёт прямо
    // в списке, отдельной панели над ним больше нет.
    const [response, pendingRes] = await Promise.all([
      api.get(`/tournaments/${tournamentId}/participants`),
      isManager
        ? api.get(`/tournaments/${tournamentId}/registrations`).catch(() => null)
        : Promise.resolve(null),
    ]);

    const pending: TournamentParticipant[] = (
      (pendingRes?.data?.registrations ?? []) as Array<Record<string, unknown>>
    )
      .filter((r) => r["status"] === "Pending")
      .map((r) => ({
        id: String(r["id"] ?? ""),
        userId: String(r["userId"] ?? ""),
        userDisplayName: String(r["userDisplayName"] ?? ""),
        riotAccountGameName: String(r["riotAccountGameName"] ?? ""),
        riotAccountTagLine: String(r["riotAccountTagLine"] ?? ""),
        riotAccountRegion: (r["riotAccountRegion"] as string | undefined) ?? undefined,
        mainRole: (r["mainRole"] as string | undefined) ?? undefined,
        secondaryRole: (r["secondaryRole"] as string | undefined) ?? undefined,
        desiredRole: String(r["desiredRole"] ?? ""),
        points: Number(r["points"] ?? 0),
        moderatorAdjustedPoints:
          (r["moderatorAdjustedPoints"] as number | undefined) ?? undefined,
        playerNote: (r["playerNote"] as string | undefined) ?? undefined,
        status: "Pending",
        registeredAt: String(r["registeredAt"] ?? ""),
        currentRank: (r["currentRank"] as string | undefined) ?? undefined,
      }));

    return [...pending, ...response.data];
  }, [tournamentId, isManager]);

  const {
    data: queriedParticipants = EMPTY_PARTICIPANTS,
    isLoading: queryLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: participantsQueryKey,
    queryFn: fetchParticipants,
    // Снапшот передан пропом — живой запрос не нужен и не должен уходить в сеть.
    // `enabled: false` не просто экономия: за счёт него `queryFn` вообще не вызывается,
    // а ниже `loading` тоже принудительно false — без этого React Query на пустом кэше
    // отдал бы isLoading:true и грид на мгновение мигнул бы скелетоном поверх готовых данных.
    enabled: Boolean(tournamentId) && !fixedParticipants,
    // Полминуты «свежести»: переключение вкладок туда-сюда не порождает запросов,
    // но список не застаивается, пока организатор разбирает заявки.
    staleTime: 30_000,
    refetchOnMount: true,
  });

  // Готовый снапшот (архив) побеждает результат запроса — при переданном пропе
  // `queriedParticipants` всегда пуст, т.к. запрос выключен выше.
  const participants = fixedParticipants ?? queriedParticipants;
  const loading = fixedParticipants ? false : queryLoading;
  const error = fixedParticipants ? null : isError ? t("participantsGrid.loadError") : null;

  // Сторонние изменения (регистрация/отмена в шапке турнира) приходят window-событием.
  // Дёргаем refetch БЕЗ показа скелетона: список уже на экране, подменять его
  // заглушкой на каждое событие — только мигание.
  useEffect(() => {
    // Снапшот архива живыми событиями регистрации не обновляется — это и есть
    // весь смысл фиксации: `refetch()` у React Query игнорирует `enabled: false`
    // при явном вызове, поэтому без этой ранней остановки событие всё равно
    // породило бы сетевой запрос в обход выключенного выше query.
    if (fixedParticipants) return;

    const onRefresh = (e: Event) => {
      const ev = e as CustomEvent<{ tournamentId?: string }>;
      if (ev?.detail?.tournamentId && ev.detail.tournamentId !== tournamentId) return;
      void refetch();
    };

    window.addEventListener("tournament:participants:refresh", onRefresh);
    return () => {
      window.removeEventListener("tournament:participants:refresh", onRefresh);
    };
  }, [tournamentId, refetch, fixedParticipants]);

  /**
   * Назначенные капитаны — из публичного /tournaments/{id}/teams (после драфта там
   * есть captainId у каждой команды). Тот же ключ, что у вкладки стримеров, поэтому
   * данные переиспользуются, а не запрашиваются второй раз.
   */
  const { data: teamsForCaptains } = useQuery({
    queryKey: ["tournaments", tournamentId, "teams"],
    queryFn: async () =>
      (
        await api.get<Array<{ captainId?: string | null }>>(
          `/tournaments/${encodeURIComponent(tournamentId)}/teams`,
        )
      ).data,
    enabled: Boolean(tournamentId) && draftCompleted,
    ...QUERY_VOLATILITY.STANDARD,
  });

  const captainUserIds = useMemo(
    () =>
      new Set(
        (teamsForCaptains ?? [])
          .map((tm) => tm.captainId)
          .filter((v): v is string => Boolean(v)),
      ),
    [teamsForCaptains],
  );

  /**
   * Соберутся ли составы при текущем бюджете. Показываем ТОЛЬКО организаторам:
   * обычному участнику это ничего не даёт, а выглядит как поломка турнира.
   * Капитаны здесь неизвестны, поэтому проверка домысливает их самыми дорогими —
   * то есть предупреждает по худшему сценарию, а не по удобному.
   */
  const feasibility = useMemo(() => {
    if (!isManager || !captainBudget || !teamsNeeded) return null;
    const approved = participants.filter((p) => p.status !== "Pending");
    if (approved.length === 0) return null;

    return checkDraftFeasibility({
      budget: captainBudget,
      teamSize: 5,
      teamsCount: teamsNeeded,
      captainCosts: [],
      poolCosts: approved.map((p) => p.moderatorAdjustedPoints ?? p.points ?? 0),
    });
  }, [isManager, captainBudget, teamsNeeded, participants]);

  /** Точечно правит строку в кэше — заменяет прежние setParticipants. */
  const patchParticipants = useCallback(
    (updater: (prev: TournamentParticipant[]) => TournamentParticipant[]) => {
      queryClient.setQueryData<TournamentParticipant[]>(
        participantsQueryKey,
        (prev) => updater(prev ?? []),
      );
    },
    [queryClient, participantsQueryKey],
  );

  // Модерация прямо в списке: подтвердить / отклонить заявку без отдельной панели.
  // Правка стоимости из строки (PATCH .../points) — как в макете у админа.
  const changePoints = async (registrationId: string, points: number) => {
    try {
      await api.patch(
        `/tournaments/${tournamentId}/registrations/${registrationId}/points`,
        { moderatorAdjustedPoints: points },
      );
      patchParticipants((prev) =>
        prev.map((p) =>
          p.id === registrationId ? { ...p, moderatorAdjustedPoints: points } : p,
        ),
      );
      toast.success(t("participantsGrid.costUpdated"));
    } catch (e) {
      toast.error(extractApiErrorMessage(e, t("participantsGrid.costUpdateFailed")));
    }
  };

  /**
   * Сброс ручной правки очков: возвращает игроку цену, посчитанную алгоритмом.
   * Раньше отменить свою правку было нечем — оставалось поставить 0, что не «сброс»,
   * а другая ручная правка: игрок становился бесплатным. Пустое значение снимает
   * ModeratorAdjustedPoints, и в силу снова вступает расчёт по рангу.
   */
  const resetPoints = async (registrationId: string) => {
    try {
      await api.patch(
        `/tournaments/${tournamentId}/registrations/${registrationId}/points`,
        { moderatorAdjustedPoints: null },
      );
      patchParticipants((prev) =>
        prev.map((p) =>
          p.id === registrationId ? { ...p, moderatorAdjustedPoints: undefined } : p,
        ),
      );
      toast.success(t("participantsGrid.costReset"));
      // Расчётная цена лежит на сервере — перечитываем, чтобы показать её, а не пустоту.
      void refetch();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, t("participantsGrid.costResetFailed")));
    }
  };

  // Смена роли из меню строки (PATCH .../role) — чтобы безролевых не гонять
  // переоформлять заявку.
  const changeRole = async (registrationId: string, role: string) => {
    try {
      await api.patch(
        `/tournaments/${tournamentId}/registrations/${registrationId}/role`,
        { mainRole: role },
      );
      patchParticipants((prev) =>
        prev.map((p) => (p.id === registrationId ? { ...p, mainRole: role } : p)),
      );
      toast.success(t("participantsGrid.roleUpdated"));
    } catch (e) {
      toast.error(extractApiErrorMessage(e, t("participantsGrid.roleUpdateFailed")));
    }
  };

  /**
   * Очистка заметки организатором. Заметку видят все, а сам игрок может убрать её
   * только через свою заявку — если он написал неуместное и не убирает, рычаг нужен
   * здесь.
   */
  const clearNote = async (registrationId: string) => {
    try {
      await api.patch(
        `/tournaments/${tournamentId}/registrations/${registrationId}/note`,
        { playerNote: "" },
      );
      patchParticipants((prev) =>
        prev.map((p) => (p.id === registrationId ? { ...p, playerNote: undefined } : p)),
      );
      toast.success(t("participantsGrid.noteDeleted"));
    } catch (e) {
      toast.error(extractApiErrorMessage(e, t("participantsGrid.noteDeleteFailed")));
    }
  };

  const moderate = async (
    registrationId: string,
    action: "approve" | "reject" | "blacklist",
  ) => {
    setModeratingId(registrationId);
    const target = participants.find((p) => p.id === registrationId);
    try {
      if (action === "blacklist") {
        if (!target) return;
        await api.post(`/tournaments/${tournamentId}/blacklist/${target.userId}`);
      } else {
        await api.post(
          `/tournaments/${tournamentId}/registrations/${registrationId}/${action}`,
        );
      }
      patchParticipants((prev) =>
        action === "approve"
          ? prev.map((p) => (p.id === registrationId ? { ...p, status: "Approved" } : p))
          : prev.filter((p) => p.id !== registrationId),
      );
      toast.success(
        action === "approve"
          ? t("participantsGrid.applicationApproved")
          : action === "blacklist"
            ? t("participantsGrid.playerDisqualified")
            : t("participantsGrid.applicationRejected"),
      );
      if (action === "blacklist") {
        // Дисквалификация теперь и на бэке гасит заявку (не только локальный кэш этого
        // грида) — счётчики участников/спотов в шапке турнира (Zustand-стор) и другие
        // React Query консьюмеры ["tournaments", id]/["tournament", id] иначе не узнают
        // об изменении до ручного обновления страницы. Тот же паттерн, что и
        // refreshAfterRegistrationChange в tournament-actions.tsx.
        void queryClient.invalidateQueries({ queryKey: tournamentKeys.detail(tournamentId) });
        void refreshTournamentFromStore(tournamentId);
        try {
          window.dispatchEvent(
            new CustomEvent("tournament:participants:refresh", {
              detail: { tournamentId },
            }),
          );
        } catch {
          // ignore
        }
      }
    } catch (e) {
      toast.error(extractApiErrorMessage(e, t("participantsGrid.actionFailed")));
    } finally {
      setModeratingId(null);
    }
  };

  const pendingCount = useMemo(
    () => participants.filter((p) => p.status === "Pending").length,
    [participants],
  );

  const captainCount = useMemo(
    () => participants.filter((p) => isAppointedCaptainOf(p, captainUserIds)).length,
    [participants, captainUserIds],
  );

  // Twitch-статусы игроков (KAN-93) — один батч-запрос на страницу турнира.
  const { data: streams } = useTournamentStreams(tournamentId);
  const streamByUserId = useMemo(
    () => new Map((streams?.playerStreams ?? []).map((s) => [s.userId, s])),
    [streams],
  );

  const roleLabels: Record<string, string> = useMemo(
    () => ({
      Top: t("participantsGrid.roleTop"),
      Jungle: t("participantsGrid.roleJungle"),
      Mid: t("participantsGrid.roleMid"),
      Adc: t("participantsGrid.roleAdc"),
      Support: t("participantsGrid.roleSupport"),
    }),
    [t],
  );

  // Пороги стоимости считаем от реальных данных: шкала очков у каждого турнира своя.
  const costThresholds = useMemo(() => {
    const values = participants.map(costOf).sort((a, b) => a - b);
    if (values.length === 0) return { low: 0, high: 0 };
    const at = (q: number) => values[Math.min(values.length - 1, Math.floor(values.length * q))] ?? 0;
    return { low: at(1 / 3), high: at(2 / 3) };
  }, [participants]);

  const roleCounts = useMemo(() => {
    const acc: Record<string, number> = { Top: 0, Jungle: 0, Mid: 0, Adc: 0, Support: 0 };
    let noRole = 0;
    for (const p of participants) {
      if (isKnownRole(p.mainRole)) acc[p.mainRole] = (acc[p.mainRole] ?? 0) + 1;
      else noRole += 1;
    }
    return { byRole: acc, noRole };
  }, [participants]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = participants.filter((p) => {
      if (q) {
        const riot = `${p.riotAccountGameName}#${p.riotAccountTagLine}`.toLowerCase();
        if (!p.userDisplayName?.toLowerCase().includes(q) && !riot.includes(q)) return false;
      }
      if (onlyPending && p.status !== "Pending") return false;
      if (onlyShortlist && !shortlist.has(p.userId)) return false;
      if (onlyCaptains && !isAppointedCaptainOf(p, captainUserIds)) return false;
      if (roleFilter === "none" && isKnownRole(p.mainRole)) return false;
      if (roleFilter !== "all" && roleFilter !== "none" && p.mainRole !== roleFilter) return false;
      if (rankFilter !== "all" && rankTier(rankOf(p)) !== rankFilter) return false;
      if (costFilter !== "all") {
        const c = costOf(p);
        if (costFilter === "top" && c < costThresholds.high) return false;
        if (costFilter === "mid" && (c < costThresholds.low || c >= costThresholds.high)) return false;
        if (costFilter === "low" && c >= costThresholds.low) return false;
      }
      return true;
    });

    const byName = (a: TournamentParticipant, b: TournamentParticipant) =>
      (a.userDisplayName || "").localeCompare(b.userDisplayName || "", "ru");
    list.sort((a, b) => {
      if (sort === "cost") return costOf(b) - costOf(a) || byName(a, b);
      if (sort === "cost-asc") return costOf(a) - costOf(b) || byName(a, b);
      if (sort === "nick") return byName(a, b);
      return (
        new Date(a.registeredAt).getTime() - new Date(b.registeredAt).getTime()
      );
    });
    return list;
  }, [
    participants,
    query,
    roleFilter,
    rankFilter,
    costFilter,
    costThresholds,
    sort,
    onlyPending,
    onlyShortlist,
    shortlist,
    onlyCaptains,
    captainUserIds,
  ]);

  /**
   * Группы по РАНГУ, а не по очкам.
   *
   * Раньше группировали по стоимости, и заголовок перечислял ранги, которые в группу
   * попали: выходило «UNRANKED · DIAMOND · EMERALD» — набор без смысла, потому что
   * шкала очков у каждого турнира своя, и одна цена смешивает разные ранги.
   * По рангу группы читаются сразу, а разброс цен внутри показываем подписью.
   */
  const tiers = useMemo(() => {
    const groups = new Map<string, TournamentParticipant[]>();
    for (const p of filtered) {
      const tier = rankTierName(rankOf(p));
      if (!groups.has(tier)) groups.set(tier, []);
      groups.get(tier)!.push(p);
    }

    const entries = [...groups.entries()].sort((a, b) => {
      const diff = rankWeight(a[0]) - rankWeight(b[0]);
      return sort === "cost-asc" ? -diff : diff;
    });

    // Разброс очков в группе раньше показывали справа в заголовке — владелец
    // турнира попросил убрать: сами очки уже видны в строке каждого игрока,
    // а диапазон в шапке только шумел.
    return entries.map(([tier, players]) => ({ tier, players }));
  }, [filtered, sort]);

  // Строки внутри тира уже идут в порядке текущей сортировки (см. tiers выше) —
  // "ступеньку" на смене баллов имеет смысл рисовать, только пока сортировка
  // реально ведётся по баллам. При сортировке по нику/дате соседство по цене
  // случайно, и линия была бы шумом, а не сигналом.
  const sortedByCost = sort === "cost" || sort === "cost-asc";

  const pointsRange = useMemo(() => {
    if (participants.length === 0) return "";
    const values = participants.map(costOf);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return min === max
      ? `${max} ${tCommon("pointsShort")}`
      : `${min}–${max} ${tCommon("pointsShort")}`;
  }, [participants, tCommon]);

  // ── Состояния ────────────────────────────────────────────────────────────────
  if (loading) return <ParticipantsSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <TriangleAlert className="h-8 w-8 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (participants.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2.5 rounded-2xl border border-dashed border-border bg-card px-6 py-16 text-center">
        <Users className="h-8 w-8 text-muted-foreground" aria-hidden />
        <h3 className="text-base font-semibold text-foreground">
          {t("participantsGrid.notFound")}
        </h3>
        <p className="max-w-sm text-sm text-muted-foreground">
          {t("participantsGrid.emptyHint")}
        </p>
      </div>
    );
  }

  // Порядок и ширины — по правкам владельца: Игрок · Баллы · Riot ID · Роль ·
  // Топ-чемпионы · Ссылки. У админа колонки очков и ссылок шире (инпут и меню).
  // Колонку «Ранг» убрали — ранг теперь виден в заголовке тир-группы (иконка
  // + подпись), а не дублируется чипом в каждой строке.
  // Колонку «2-я» (вторая роль) убрали — вторая роль теперь показывается через
  // слеш рядом с основной («Мид / Лес») в той же колонке «Роль», отдавшей себе
  // освободившуюся ширину.
  // Плотность строк зафиксирована на компактной (бывшей «Плотный») — второй
  // режим убран вместе с переключателем в панели фильтров.
  // Колонка «Роль» теперь показывает только иконки (без подписи текстом —
  // правка владельца), поэтому ей достаточно места под 1-2 иконки + слеш;
  // освободившуюся ширину отдаём «Игроку» и Riot ID — они чаще всего
  // обрезаются.
  // Колонка очков стала на пару пикселей шире (76→84 / 64→70) — теперь под
  // индикатор ручной правки (✎) слева от числа всегда зарезервирован слот
  // фиксированной ширины (даже когда правки не было и он невидим), иначе
  // цифра/инпут прыгали бы между строками с правкой и без.
  const gridCols = [
    ...(isCaptain ? ["32px"] : []),
    "minmax(180px,1.9fr)",
    isManager ? "84px" : "70px",
    "minmax(160px,1.2fr)",
    "minmax(64px,0.5fr)",
    "136px",
    isManager ? "92px" : "64px",
  ].join(" ");

  return (
    <div className="space-y-4">
      {/* Предупреждение организатору: при текущем бюджете составы не соберутся.
          Только для управляющих турниром — участнику это ничего не даёт, а выглядит
          как поломка. Показываем ЗДЕСЬ, а не при старте драфта: когда драфт пошёл
          и последний капитан не может добрать пятого, исправлять поздно. */}
      {feasibility && !feasibility.feasible ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-destructive/40 bg-destructive/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2.5">
            <TriangleAlert className="mt-0.5 h-4 w-4 flex-none text-destructive" aria-hidden />
            <div className="text-sm">
              <div className="font-semibold text-destructive">
                {feasibility.reason === "not_enough_players"
                  ? t("participantsGrid.draftNotEnoughPlayers")
                  : t("participantsGrid.draftNotEnoughPoints")}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {/* Склейка числа со словом («1 балла») ломается на склонениях и не
                    переводится: форму выбирает словарь через ICU-plural. */}
                {feasibility.reason === "not_enough_players"
                  ? t("participantsGrid.draftNeedPlayers", {
                      needed: feasibility.playersNeeded,
                      available: feasibility.playersAvailable,
                    })
                  : t("participantsGrid.draftShortfall", {
                      shortfall: feasibility.shortfall,
                      budget: feasibility.minBudgetNeeded,
                    })}
              </div>
            </div>
          </div>
          {draftSettingsHref && feasibility.reason !== "not_enough_players" ? (
            <a
              href={draftSettingsHref}
              className="inline-flex flex-none items-center justify-center rounded-lg bg-destructive px-3 py-2 text-xs font-semibold text-destructive-foreground transition-opacity hover:opacity-90"
            >
              {t("participantsGrid.fixBudget")}
            </a>
          ) : null}
        </div>
      ) : null}

      {/* Сводка: всего + по ролям + без роли (плитки работают как быстрый фильтр) */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-7">
        <div className="flex flex-col justify-center gap-0.5 rounded-2xl border border-border bg-card px-3.5 py-3 shadow-sm">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {t("participantsGrid.totalInPool")}
          </span>
          <span className="flex items-baseline gap-2">
            <span className="text-2xl font-bold leading-tight text-foreground">
              {participants.length}
            </span>
            <span className="text-xs text-muted-foreground">{pointsRange}</span>
          </span>
        </div>

        {roleOrder.map((role) => {
          const count = roleCounts.byRole[role] ?? 0;
          const need = teamsNeeded ?? 0;
          const short = need > 0 && count < need;
          const active = roleFilter === role;
          return (
            <button
              key={role}
              type="button"
              onClick={() => setRoleFilter(active ? "all" : role)}
              className={cn(
                "flex flex-col justify-center gap-1 rounded-2xl border bg-card px-3.5 py-3 text-left shadow-sm transition-colors hover:border-muted-foreground/40",
                active ? "border-primary" : "border-border",
              )}
            >
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <RoleIcon role={role} size={14} />
                <span className="text-[11px] uppercase tracking-wide">
                  {roleLabels[role] ?? role}
                </span>
              </span>
              <span className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold leading-tight text-foreground">{count}</span>
                {need > 0 && (
                  <span
                    className={cn(
                      "text-[11px]",
                      short ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground",
                    )}
                  >
                    {short ? tUi("litTournamentParticipantsGrid.nuzhno", { need }) : tUi("litTournamentParticipantsGrid.iz", { need })}
                  </span>
                )}
              </span>
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => setRoleFilter(roleFilter === "none" ? "all" : "none")}
          className={cn(
            "flex flex-col justify-center gap-1 rounded-2xl border bg-card px-3.5 py-3 text-left shadow-sm transition-colors",
            roleFilter === "none"
              ? "border-amber-500/60"
              : "border-border hover:border-amber-500/40",
          )}
        >
          <span className="text-[11px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
            {t("participantsGrid.noRole")}
          </span>
          <span className="text-xl font-bold leading-tight text-amber-600 dark:text-amber-400">
            {roleCounts.noRole}
          </span>
        </button>
      </div>

      {/* Панель: поиск, фильтры, сортировка, плотность */}
      <div className="sticky top-[var(--gl-sticky-top)] z-20 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card/95 p-2.5 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("participantsGrid.searchPlaceholder")}
            className="h-9 pl-8"
          />
        </div>

        <FilterSelect
          value={roleFilter}
          onChange={(v) => setRoleFilter(v as typeof roleFilter)}
          width="w-[140px]"
          label={t("participantsGrid.filterRoleLabel")}
          options={[
            { value: "all", label: t("participantsGrid.allRoles") },
            ...roleOrder.map((r) => ({ value: r, label: roleLabels[r] ?? r })),
            { value: "none", label: t("participantsGrid.noRole") },
          ]}
        />
        <FilterSelect
          value={rankFilter}
          onChange={(v) => setRankFilter(v as typeof rankFilter)}
          width="w-[180px]"
          label={t("participantsGrid.filterRankLabel")}
          options={[
            { value: "all", label: t("participantsGrid.anyRank") },
            { value: "high", label: "Master+" },
            { value: "mid", label: "Diamond · Emerald" },
            { value: "low", label: t("participantsGrid.platinumAndBelow") },
          ]}
        />
        <FilterSelect
          value={costFilter}
          onChange={(v) => setCostFilter(v as typeof costFilter)}
          width="w-[190px]"
          label={t("participantsGrid.filterCostLabel")}
          options={[
            { value: "all", label: t("participantsGrid.anyCost") },
            { value: "top", label: tUi("litTournamentParticipantsGrid.dorogie", { n: costThresholds.high }) },
            { value: "mid", label: t("participantsGrid.costMid") },
            { value: "low", label: tUi("litTournamentParticipantsGrid.deshevye", { n: costThresholds.low }) },
          ]}
        />
        <FilterSelect
          value={sort}
          onChange={(v) => setSort(v as typeof sort)}
          width="w-[180px]"
          label={t("participantsGrid.sortLabel")}
          options={[
            { value: "cost", label: t("participantsGrid.sortCostDesc") },
            { value: "cost-asc", label: t("participantsGrid.sortCostAsc") },
            { value: "nick", label: t("participantsGrid.sortName") },
            { value: "date", label: t("participantsGrid.sortRegistered") },
          ]}
        />

        {/* Только назначенные капитаны — тот же бейдж «Капитан» у строки/карточки,
            что и текстовая пометка над ником (isAppointedCaptainOf). Прячем кнопку,
            пока капитанов ещё нет: до назначения слотов фильтр всё равно ничего
            не покажет и только займёт место в и так плотной панели. */}
        {captainCount > 0 && (
          <button
            type="button"
            onClick={() => setOnlyCaptains((v) => !v)}
            aria-pressed={onlyCaptains}
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-medium transition-colors",
              onlyCaptains
                ? "border-amber-500/60 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                : "border-input bg-background text-muted-foreground hover:text-foreground",
            )}
          >
            <Crown className="h-3.5 w-3.5" />
            {tCommon("captains")} · {captainCount}
          </button>
        )}

        {isCaptain && (
          <button
            type="button"
            onClick={() => setOnlyShortlist((v) => !v)}
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-medium transition-colors",
              onlyShortlist
                ? "border-primary bg-primary/10 text-foreground"
                : "border-input bg-background text-muted-foreground hover:text-foreground",
            )}
          >
            <Star className={cn("h-3.5 w-3.5", onlyShortlist && "fill-current")} />
            Избранные · {shortlist.size}
          </button>
        )}

        {isManager && pendingCount > 0 && (
          <button
            type="button"
            onClick={() => setOnlyPending((v) => !v)}
            className={cn(
              "h-9 rounded-lg border px-3 text-xs font-medium transition-colors",
              onlyPending
                ? "border-amber-500/60 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                : "border-input bg-background text-muted-foreground hover:text-foreground",
            )}
          >
            Не подтверждены · {pendingCount}
          </button>
        )}
      </div>

      {/* Таблица по тирам стоимости (десктоп) */}
      {isDesktop ? (
      <div className="hidden overflow-clip rounded-2xl border border-border bg-card shadow-sm lg:block">
        <div
          className="grid items-center gap-3 border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
          style={{ gridTemplateColumns: gridCols }}
        >
          {isCaptain && <div className="text-center">★</div>}
          <div>{tUi("tournamentParticipantsGrid.igrok")}</div>
          <div className="text-right">{t("participantsGrid.colPoints")}</div>
          <div>Riot ID</div>
          <div>{tUi("tournamentParticipantsGrid.rol")}</div>
          <div>{tUi("tournamentParticipantsGrid.topChempiony")}</div>
          <div className="text-right">{tUi("tournamentParticipantsGrid.ssylki")}</div>
        </div>

        {tiers.map((tier) => (
          <div key={tier.tier}>
            <TierGroupHeader
              tier={tier.tier}
              count={tier.players.length}
            />
            {tier.players.map((p, idx) => {
              // Границу баллов рисуем ТОЛЬКО когда список реально отсортирован
              // по очкам — сортировка переключаемая (по нику, по дате), и там
              // соседство по цене ничего не значит, линия была бы шумом.
              // Разделитель строки — это её собственный border-b (граница
              // СНИЗУ), поэтому сравниваем со следующим уже отрендеренным
              // соседом, а не с предыдущим и не по индексу — так это работает
              // при любом направлении сортировки по баллам (убывание/возрастание).
              const next = tier.players[idx + 1];
              const costBoundary =
                sortedByCost && next != null && costOf(next) !== costOf(p);
              return (
                <ParticipantRow
                  key={p.id}
                  participant={p}
                  stream={streamByUserId.get(p.userId)}
                  gridCols={gridCols}
                  roleLabels={roleLabels}
                  isManager={isManager}
                  isCaptain={isCaptain}
                  inShortlist={shortlist.has(p.userId)}
                  onToggleShortlist={toggleShortlist}
                  onModerate={(_, action) => setConfirmDialog({ participant: p, action })}
                  onPoints={changePoints}
                  onRole={() => setRoleDialog(p)}
                  onClearNote={isManager ? clearNote : undefined}
                  onResetPoints={isManager ? resetPoints : undefined}
                  moderating={moderatingId === p.id}
                  isAppointedCaptain={isAppointedCaptainOf(p, captainUserIds)}
                  costBoundary={costBoundary}
                />
              );
            })}
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="px-6 py-11 text-center text-sm text-muted-foreground">
            {t("participantsGrid.nobodyFound")}
          </div>
        )}

        <CostExplainer />
      </div>
      ) : (
      /* Мобильные карточки */
      <div className="space-y-2.5 lg:hidden">
        {tiers.map((tier) => (
          <div key={tier.tier} className="space-y-2">
            <TierGroupHeader
              tier={tier.tier}
              count={tier.players.length}
              className="rounded-xl"
            />
            {tier.players.map((p) => (
              <ParticipantCard
                key={p.id}
                participant={p}
                stream={streamByUserId.get(p.userId)}
                roleLabels={roleLabels}
                isAppointedCaptain={isAppointedCaptainOf(p, captainUserIds)}
              />
            ))}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
            {t("participantsGrid.nobodyFound")}
          </div>
        )}
      </div>
      )}

      {/* Футер как в макете: что показано и сколько неподтверждённых + пояснение
          про то, откуда берётся стоимость. */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
        <span>
          Показано {filtered.length} из {participants.length}
          {isManager && pendingCount > 0 ? ` · ${tUi("litTournamentParticipantsGrid.nePodtverzhdeny", { count: pendingCount })}` : ""}
        </span>
        <span>{tUi("tournamentParticipantsGrid.stoimostSchitaetsyaPoRangu")}</span>
      </div>

      {/* Смена роли — отдельная модалка */}
      <Dialog open={roleDialog != null} onOpenChange={(o) => !o && setRoleDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{tUi("tournamentParticipantsGrid.izmenitRol")}</DialogTitle>
            <DialogDescription>
              {roleDialog?.userDisplayName} · {roleDialog?.riotAccountGameName}#
              {roleDialog?.riotAccountTagLine}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {roleOrder.map((r) => (
              <Button
                key={r}
                variant={roleDialog?.mainRole === r ? "default" : "outline"}
                className="justify-start gap-2"
                onClick={() => {
                  if (roleDialog) changeRole(roleDialog.id, r);
                  setRoleDialog(null);
                }}
              >
                <RoleIcon role={r} size={14} />
                {roleLabels[r] ?? r}
              </Button>
            ))}
            {/* Пустая роль = «любая»: игрок готов на любую линию. Раньше это называлось
                «снять роль», но по смыслу это не отсутствие роли — в драфте такой игрок
                подходит капитану под любой свободный слот. */}
            <Button
              variant="ghost"
              className="justify-start text-muted-foreground"
              onClick={() => {
                if (roleDialog) changeRole(roleDialog.id, "");
                setRoleDialog(null);
              }}
            >
              {t("participantsGrid.noRole")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Подтверждение заявки / отклонение / исключение */}
      <AlertDialog
        open={confirmDialog != null}
        onOpenChange={(o) => !o && setConfirmDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog?.action === "approve"
                ? tUi("litTournamentParticipantsGrid.podtverditZayavku", { name: confirmDialog?.participant.userDisplayName ?? "" })
                : confirmDialog?.action === "reject"
                  ? tUi("litTournamentParticipantsGrid.otklonitZayavku", { name: confirmDialog?.participant.userDisplayName ?? "" })
                  : tUi("litTournamentParticipantsGrid.diskvalifitsirovat", { name: confirmDialog?.participant.userDisplayName ?? "" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.action === "approve"
                ? t("participantsGrid.approveHint")
                : confirmDialog?.action === "reject"
                  ? t("participantsGrid.rejectHint")
                  : t("participantsGrid.disqualifyHint")}
              {confirmDialog ? (
                <span className="mt-2 block font-mono text-xs">
                  {confirmDialog.participant.riotAccountGameName}#
                  {confirmDialog.participant.riotAccountTagLine}
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tUi("tournamentParticipantsGrid.otmena")}</AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmDialog?.action === "approve"
                  ? undefined
                  : "bg-destructive text-white hover:bg-destructive/90"
              }
              onClick={() => {
                if (confirmDialog) moderate(confirmDialog.participant.id, confirmDialog.action);
                setConfirmDialog(null);
              }}
            >
              {confirmDialog?.action === "approve"
                ? t("participantsGrid.approve")
                : confirmDialog?.action === "reject"
                  ? t("participantsGrid.reject")
                  : t("participantsGrid.disqualify")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Мелкие части ───────────────────────────────────────────────────────────────

function FilterSelect({
  value,
  onChange,
  options,
  width,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  width?: string;
  /** Доступное имя: у role="combobox" оно не берётся из выбранного значения. */
  label: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className={cn("h-9", width)} aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Заголовок тир-группы: большая полупрозрачная иконка ранга под подписью,
 * растворяющаяся к краям через mask-image (утверждённый макет владельца).
 * Один компонент переиспользуется в десктопной таблице и в мобильных
 * карточках — контейнер сам обрезает иконку по `overflow: hidden`, поэтому
 * на узких экранах (375px) она просто уходит за край, а не ломает раскладку.
 *
 * Подпись — тир БЕЗ дивизиона («MASTER», не «MASTER I»): `tier` уже приходит
 * из rankTierName (см. группировку в tiers), которая берёт только первое
 * слово ранга. Иконка ищется по тому же тиру через getRiotRankImageUrl.
 *
 * Прозрачность иконки — токен `--tier-icon-opacity` (roster-design.css):
 * значение из макета (.62) держим для тёмной темы, для светлой оно ниже —
 * на белом фоне тот же процент читается грязным пятном.
 *
 * Число игроков склоняется по-русски (1 игрок / 2 игрока / 5 игроков) —
 * `pluralizePlayers` из shared/lib/pluralize-ru. Диапазон очков, который
 * раньше показывался справа («27–33 очк.»), убран по просьбе владельца:
 * очки и так видны в каждой строке, а диапазон в шапке только шумел.
 */
function TierGroupHeader({
  tier,
  count,
  className,
}: {
  tier: string;
  count: number;
  className?: string;
}) {
  const tCommon = useTranslations("Common");
  const iconUrl = getRiotRankImageUrl(tier);
  return (
    <div className={cn("relative h-14 overflow-hidden bg-muted/40", className)}>
      {iconUrl ? (
        <div
          aria-hidden
          className="r-tiergroup__icon"
          style={{ backgroundImage: `url(${iconUrl})` }}
        />
      ) : null}
      <div
        className="relative z-[2] flex h-full items-center gap-2.5 pr-4"
        style={{ paddingLeft: iconUrl ? 74 : 16 }}
      >
        <span className="text-sm font-semibold text-foreground">{tier}</span>
        <span className="text-xs text-muted-foreground">
          {tCommon("plural.players", { count })}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}

/**
 * Заметка игрока о себе. Место в строке дорогое, а заметка нужна не всегда —
 * поэтому иконка, а текст по наведению (shadcn Tooltip). Без заметки не рисуем
 * ничего: пустая иконка-заглушка только зашумляла бы список.
 */
function PlayerNoteHint({ note }: { note?: string }) {
  // Хук до раннего выхода: порядок вызовов React должен быть постоянным.
  const t = useTranslations("Tournaments.participantsGrid");

  if (!note?.trim()) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="flex-none cursor-help text-muted-foreground transition-colors hover:text-foreground"
            role="img"
            aria-label={t("playerNote")}
          >
            <StickyNote className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        {/* whitespace-pre-line: заметку пишут в textarea с переносами, а
            white-space: normal их проглатывал и склеивал строки в одну.
            leading-relaxed — у Exo 2 хвост «у» уходит на 2.5px при собственном
            descent шрифта в 2px, на плотной строке ему тесно.
            Зазор под стрелку добавляет сам TooltipContent. */}
        <TooltipContent
          side="top"
          className="max-w-[260px] whitespace-pre-line text-xs leading-relaxed"
        >
          {note}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PlayerIdentity({
  participant,
  stream,
  pending,
  isAppointedCaptain,
}: {
  participant: TournamentParticipant;
  stream?: PlayerStreamData;
  pending?: boolean;
  isAppointedCaptain?: boolean;
}) {
  const t = useTranslations("Tournaments");
  const initial = (participant.userDisplayName || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {/* Radix сам переключается на AvatarFallback, когда картинка не загрузилась.
          Раньше здесь был голый next/image: у трети участников аватар в Discord
          сменён, старая ссылка отдаёт 404, и на её месте браузер рисовал свой
          «?». Буква-заглушка была только для пустого URL — то есть ровно для
          того случая, когда она и не нужна. */}
      <Avatar className="h-[22px] w-[22px] flex-none border border-border">
        <AvatarImage
          src={withDiscordAvatarSize(participant.userAvatarUrl, 22)}
          alt=""
          loading="lazy"
          decoding="async"
          className="object-cover"
        />
        <AvatarFallback className="text-[10px] font-semibold text-muted-foreground">
          {initial}
        </AvatarFallback>
      </Avatar>
      <span className="flex min-w-0 items-center gap-1.5">
        <ProfilePreviewTrigger userId={participant.userId}>
          <button
            type="button"
            className="min-w-0 truncate text-left text-sm font-semibold text-foreground hover:text-primary hover:underline"
            onClick={() => window.open(`/u/${participant.userId}`, "_blank")}
          >
            {participant.userDisplayName || t("participantsGrid.noName")}
          </button>
        </ProfilePreviewTrigger>
        <TwitchStatusIcon stream={stream} />
        {/* Пометка — только у НАЗНАЧЕННОГО капитана (слот в TournamentDraftCaptains,
            заполняется организатором до старта драфта). Пожелание «хочу быть
            капитаном» здесь не показываем: человек ещё никем не назначен, и пометка
            читалась бы как факт. Пожелания видит организатор в панели драфта.
            Текстовый бейдж, а не корона — по просьбе владельца турнира. */}
        {isAppointedCaptain && (
          <Badge
            variant="role-captain"
            title={t("participantsGrid.teamCaptain")}
            className="flex-none text-[10px] uppercase"
          >
            {t("participantsGrid.captainShort")}
          </Badge>
        )}
        <PlayerNoteHint note={participant.playerNote} />
        {pending && (
          <Badge variant="warning" className="flex-none text-[10px] uppercase">
            {t("participantsGrid.notConfirmed")}
          </Badge>
        )}
      </span>
    </div>
  );
}

/**
 * Роль игрока — только иконки, без подписи (основная + вторая через слеш),
 * без выбранной роли — иконка-астериск (Asterisk из lucide-react) вместо
 * текста «Любая» — по правке владельца турнира, который смотрел прод: раньше
 * рядом с иконкой ещё дублировалось название роли текстом, теперь только
 * иконка — так колонка уже, а название роли остаётся доступным через
 * title/aria-label по наведению на саму иконку. Общий для десктопной строки
 * и мобильной карточки, чтобы роль читалась одинаково в обоих местах.
 *
 * Вторая роль без основной не показывается: «любая роль» уже покрывает
 * любую позицию, уточнять её вторым слешем нечем.
 */
function RoleCombo({
  mainRole,
  secondaryRole,
  roleLabels,
  size = 18,
  className,
}: {
  mainRole?: string;
  secondaryRole?: string;
  roleLabels: Record<string, string>;
  size?: number;
  className?: string;
}) {
  const t = useTranslations("Tournaments.participantsGrid");
  const mainLabel = mainRole ? roleLabels[mainRole] ?? mainRole : t("noRole");
  const secondaryLabel =
    mainRole && secondaryRole ? roleLabels[secondaryRole] ?? secondaryRole : undefined;

  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground",
        className,
      )}
      title={secondaryLabel ? `${mainLabel} / ${secondaryLabel}` : mainLabel}
    >
      {mainRole ? (
        <span
          title={mainLabel}
          aria-label={mainLabel}
          role="img"
          className="inline-flex flex-none"
        >
          <RoleIcon role={mainRole} size={size} />
        </span>
      ) : (
        <span
          title={t("noRole")}
          aria-label={t("noRole")}
          role="img"
          className="inline-flex flex-none text-muted-foreground"
        >
          <Asterisk style={{ width: size, height: size }} />
        </span>
      )}
      {secondaryLabel && (
        <>
          <span className="flex-none text-muted-foreground/50">/</span>
          <span
            title={secondaryLabel}
            aria-label={secondaryLabel}
            role="img"
            className="inline-flex flex-none"
          >
            <RoleIcon role={secondaryRole} size={size} />
          </span>
        </>
      )}
    </span>
  );
}

function ChampionIcons({ participant }: { participant: TournamentParticipant }) {
  const list = participant.topChampionMasteries?.slice(0, 5) ?? [];
  if (list.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-1">
      {list.map((m) => (
        <ChampionIcon
          key={`${participant.userId}-${m.championId}`}
          name={m.championName}
          size={24}
          className="h-6 w-6"
          title={`${m.championName} · ${m.championPoints}`}
        />
      ))}
    </div>
  );
}

const ParticipantRow = React.memo(function ParticipantRow({
  participant,
  stream,
  gridCols,
  roleLabels,
  isManager,
  isCaptain,
  inShortlist,
  onToggleShortlist,
  onModerate,
  onPoints,
  onRole,
  onClearNote,
  onResetPoints,
  moderating,
  isAppointedCaptain,
  costBoundary,
}: {
  participant: TournamentParticipant;
  stream?: PlayerStreamData;
  gridCols: string;
  roleLabels: Record<string, string>;
  isManager?: boolean;
  isCaptain?: boolean;
  inShortlist?: boolean;
  onToggleShortlist?: (playerUserId: string) => void;
  onModerate?: (registrationId: string, action: "approve" | "reject" | "blacklist") => void;
  onPoints?: (registrationId: string, points: number) => void;
  onRole?: (registrationId: string, role: string) => void;
  onClearNote?: (registrationId: string) => void;
  onResetPoints?: (registrationId: string) => void;
  moderating?: boolean;
  /** Назначенный капитан команды (после драфта) — не «хочет быть», а уже является. */
  isAppointedCaptain?: boolean;
  /**
   * Ниже идёт игрок с ДРУГИМ количеством баллов (только когда список внутри
   * тира отсортирован по баллам — считает вызывающий код, сравнивая с
   * соседом по уже отрендеренному, отсортированному массиву). Рисуем
   * разделитель контрастнее обычного, чтобы на глаз было видно смену цены
   * внутри тира (29 → 28), а не только по цифре в колонке очков.
   */
  costBoundary?: boolean;
}) {
  const t = useTranslations("Tournaments.participantsGrid");
  const opggUrl = getOpggUrl(
    participant.riotAccountGameName,
    participant.riotAccountTagLine,
    participant.riotAccountRegion,
  );
  const riot = `${participant.riotAccountGameName}#${participant.riotAccountTagLine}`;
  const isPending = participant.status === "Pending";
  const [pointsDraft, setPointsDraft] = useState(String(costOf(participant)));
  useEffect(() => setPointsDraft(String(costOf(participant))), [participant]);

  const commitPoints = () => {
    const parsed = Number(pointsDraft);
    if (!Number.isFinite(parsed) || parsed === costOf(participant)) {
      setPointsDraft(String(costOf(participant)));
      return;
    }
    onPoints?.(participant.id, parsed);
  };

  return (
    <div
      className={cn(
        // Ховер — нейтральный foreground/[0.03] (тот же приём, что и в
        // tournament-player-stats-board.tsx), а не bg-muted/40: тот совпадал
        // по цвету с заголовком тир-группы (тоже bg-muted/40), и строка под
        // курсором на миг читалась как заголовок категории.
        "grid items-center gap-3 px-4 py-1.5 transition-colors last:border-b-0 hover:bg-foreground/[0.03]",
        // «Ступенька» на границе смены баллов внутри тира — та же толщина
        // 1px, но полная непрозрачность border вместо приглушённых 60%:
        // заметно контрастнее обычного разделителя, но не режет список на
        // куски (в Master баллы почти всегда разные — жирная линия была бы
        // избыточной).
        costBoundary ? "border-b border-border" : "border-b border-border/60",
        isPending && "bg-amber-500/5",
      )}
      style={{ gridTemplateColumns: gridCols }}
    >
      {isCaptain && (
        <button
          type="button"
          title={inShortlist ? t("removeFromShortlist") : t("addToShortlist")}
          onClick={() => onToggleShortlist?.(participant.userId)}
          className={cn(
            "flex items-center justify-center transition-colors",
            inShortlist ? "text-amber-500" : "text-muted-foreground/40 hover:text-muted-foreground",
          )}
        >
          <Star className={cn("h-4 w-4", inShortlist && "fill-current")} />
        </button>
      )}

      <PlayerIdentity
        participant={participant}
        stream={stream}
        pending={isPending}
        isAppointedCaptain={isAppointedCaptain}
      />

      {/* Очки: у организатора — инлайн-правка (PATCH .../points). У всех
          остальных — только чтение, и цифра крупнее и жирнее: без поля ввода
          рядом её ничто не «подпирает» по размеру, а с рангом теперь дублирует
          только заголовок группы, так что баллы — главное число в строке.
          Индикатор ручной правки (✎) — СЛЕВА от числа (правка владельца:
          раньше был справа). Слот под него зарезервирован фиксированной
          шириной и рисуется всегда (invisible, если правки не было), иначе
          цифра/инпут прыгали бы влево-вправо между строками с правкой и без —
          так они всегда выровнены по тому же правому краю. */}
      <div className="flex items-center justify-end gap-1.5">
        <span
          title={
            participant.moderatorAdjustedPoints != null
              ? t("costAdjusted")
              : undefined
          }
          aria-hidden={participant.moderatorAdjustedPoints == null}
          className={cn(
            "flex w-3 flex-none items-center justify-center text-[10px] text-amber-600 dark:text-amber-400",
            participant.moderatorAdjustedPoints == null && "invisible",
          )}
        >
          ✎
        </span>
        {isManager && onPoints ? (
          <Input
            value={pointsDraft}
            inputMode="numeric"
            onChange={(e) => setPointsDraft(e.target.value.replace(/[^\d]/g, ""))}
            onBlur={commitPoints}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            title={t("costInPoints")}
            className="h-6 w-12 px-1 text-right text-xs tabular-nums"
          />
        ) : (
          <span className="text-lg font-extrabold tabular-nums text-foreground">
            {costOf(participant)}
          </span>
        )}
      </div>

      <a
        href={opggUrl ?? "https://www.op.gg"}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 truncate text-xs text-muted-foreground hover:text-foreground hover:underline"
        title={riot}
      >
        {riot}
      </a>

      {/* Роль — основная и вторая (если есть) в одной колонке через слеш
          («Мид / Лес»), колонку «2-я» убрали. Пустая роль = «Любая»: это
          осознанный выбор игрока, а не пробел в данных, поэтому подаём её
          спокойно — иконкой-астериском, а не жёлтым бейджем. */}
      <div className="flex min-w-0 items-center">
        <RoleCombo
          mainRole={participant.mainRole}
          secondaryRole={participant.secondaryRole}
          roleLabels={roleLabels}
        />
      </div>

      <ChampionIcons participant={participant} />

      {/* Ссылки: op.gg и меню организатора — справа. Кнопку-иконку профиля
          убрали по просьбе владельца: имя игрока в PlayerIdentity уже ведёт
          на тот же /u/{userId}, дублировать её здесь незачем. */}
      <div className="relative flex items-center justify-end gap-1.5">
        <a
          href={opggUrl ?? "https://www.op.gg"}
          target="_blank"
          rel="noopener noreferrer"
          title="op.gg"
          className="inline-flex h-6 items-center rounded-md border border-border px-1.5 text-[10px] font-bold tracking-wide text-muted-foreground hover:text-foreground"
        >
          OP.GG
        </a>
        {isManager && onModerate && (
          <>
            {/* Radix DropdownMenu вместо самописного меню на useState: то закрывалось
                ТОЛЬКО повторным кликом по троеточию, поэтому можно было открыть
                сразу несколько строк и забыть их закрытыми. Здесь клик снаружи,
                Escape, выбор пункта и открытие другого меню закрывают его сами. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title={t("more")}
                  aria-label={t("applicationActions")}
                  disabled={moderating}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[220px]">
                {isPending && (
                  <DropdownMenuItem
                    className="text-xs font-semibold text-green-600 dark:text-green-500"
                    onSelect={() => onModerate(participant.id, "approve")}
                  >
                    {t("approveApplication")}
                  </DropdownMenuItem>
                )}
                {onRole && (
                  <DropdownMenuItem
                    className="text-xs"
                    onSelect={() => onRole(participant.id, "")}
                  >
                    {t("changeRole")}
                  </DropdownMenuItem>
                )}
                {onResetPoints && participant.moderatorAdjustedPoints != null ? (
                  <DropdownMenuItem
                    className="text-xs"
                    onSelect={() => onResetPoints(participant.id)}
                  >
                    {t("resetCost")}
                  </DropdownMenuItem>
                ) : null}
                {onClearNote && participant.playerNote ? (
                  <DropdownMenuItem
                    className="text-xs"
                    onSelect={() => onClearNote(participant.id)}
                  >
                    {t("deleteNote")}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  className="text-xs text-destructive focus:text-destructive"
                  onSelect={() =>
                    onModerate(participant.id, isPending ? "reject" : "blacklist")
                  }
                >
                  {isPending ? t("rejectApplication") : t("disqualify")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  );
});

const ParticipantCard = React.memo(function ParticipantCard({
  participant,
  stream,
  roleLabels,
  noRole,
  isAppointedCaptain,
}: {
  participant: TournamentParticipant;
  stream?: PlayerStreamData;
  roleLabels: Record<string, string>;
  isManager?: boolean;
  onModerate?: (registrationId: string, action: "approve" | "reject") => void;
  moderating?: boolean;
  noRole?: boolean;
  /** Назначенный капитан команды (после драфта). */
  isAppointedCaptain?: boolean;
}) {
  const tUi = useTranslations("Ui");
  const opggUrl = getOpggUrl(
    participant.riotAccountGameName,
    participant.riotAccountTagLine,
    participant.riotAccountRegion,
  );
  const riot = `${participant.riotAccountGameName}#${participant.riotAccountTagLine}`;
  const isPending = participant.status === "Pending";
  return (
    <div className="space-y-2 rounded-2xl border border-border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <PlayerIdentity
          participant={participant}
          stream={stream}
          pending={isPending}
          isAppointedCaptain={isAppointedCaptain}
        />
        {/* Мобильная карточка инлайн-правку очков никогда не показывает (поле
            ввода есть только в десктопной таблице у организатора) — здесь
            всегда «зрительский» вид, крупный и жирный. */}
        <span className="text-lg font-extrabold tabular-nums text-foreground">
          {costOf(participant)}
        </span>
      </div>
      <a
        href={opggUrl ?? "https://www.op.gg"}
        target="_blank"
        rel="noopener noreferrer"
        className="block truncate text-xs text-muted-foreground hover:text-foreground"
      >
        {riot}
      </a>
      {/* Роль — тот же RoleCombo, что и в десктопной строке: основная + вторая
          через слеш («Мид / Лес»), без роли — иконка-астериск вместо текста
          «Любая». Раньше это были два отдельных бейджа. */}
      <div className="flex flex-wrap items-center gap-2">
        {noRole ? (
          <Badge variant="warning" className="text-[10px] uppercase">
            {tUi("tournamentParticipantsGrid.rolNeUkazana")}
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1.5">
            <RoleCombo
              mainRole={participant.mainRole}
              secondaryRole={participant.secondaryRole}
              roleLabels={roleLabels}
              size={14}
              className="text-[10px] font-semibold uppercase text-foreground"
            />
          </Badge>
        )}
        <span className="ml-auto">
          <ChampionIcons participant={participant} />
        </span>
      </div>
    </div>
  );
});

/**
 * Короткое объяснение под таблицей: откуда берутся цифры в колонке стоимости.
 * Без него список выглядит как «числа из ниоткуда», и первый же вопрос игрока —
 * «почему я стою столько». Указание op.gg здесь ещё и обязательное условие
 * их политики использования данных.
 */
function CostExplainer() {
  const t = useTranslations("Tournaments.participantsGrid");

  return (
    <div className="border-t border-border bg-muted/30 px-4 py-3">
      <p className="text-xs font-medium text-foreground">{t("costTitle")}</p>

      <ul className="mt-1.5 space-y-1 text-[11px] leading-relaxed text-muted-foreground">
        <li>
          <span className="text-foreground">{t("costRankTerm")}</span>{" "}
          {t("costRankText")}
        </li>
        <li>
          <span className="text-foreground">{t("costRegionTerm")}</span>{" "}
          {t("costRegionText")}
        </li>
        <li>
          <span className="text-foreground">{t("costPeakTerm")}</span>{" "}
          {t("costPeakText")}
        </li>
        <li>
          <span className="text-foreground">{t("costFloorTerm")}</span>{" "}
          {t("costFloorText")}
        </li>
      </ul>

      {/* Эти два случая живые люди спрашивали в Discord — отвечаем сразу, чтобы
          не объяснять каждому в личку. */}
      <p className="mt-2.5 text-xs font-medium text-foreground">{t("faqTitle")}</p>
      <ul className="mt-1 space-y-1 text-[11px] leading-relaxed text-muted-foreground">
        <li>
          <span className="text-foreground">{t("faqUnrankedTerm")}</span>{" "}
          {t("faqUnrankedText")}
        </li>
        <li>
          <span className="text-foreground">{t("faqFlexTerm")}</span>{" "}
          {t("faqFlexText")}
        </li>
        <li>
          <span className="text-foreground">{t("faqSmurfTerm")}</span>{" "}
          {t("faqSmurfText")}
        </li>
        <li>
          <span className="text-foreground">{t("faqNoHistoryTerm")}</span>{" "}
          {t("faqNoHistoryText")}
        </li>
      </ul>

      <p className="mt-2 text-[11px] text-muted-foreground">
        {t("sourceRiot")}{" "}
        <a
          href="https://op.gg"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          op.gg
        </a>
        {t("sourceManual")}
      </p>
    </div>
  );
}

function ParticipantsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-7">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-[68px] animate-pulse rounded-2xl border border-border bg-card" />
        ))}
      </div>
      <div className="h-14 animate-pulse rounded-2xl border border-border bg-card" />
      <div className="overflow-clip rounded-2xl border border-border bg-card">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-3 border-b border-border/60 px-4 py-3 last:border-b-0"
          >
            <div className="h-7 w-7 animate-pulse rounded-full bg-muted" />
            <div className="h-3 w-32 animate-pulse rounded bg-muted" />
            <div className="h-3 w-28 animate-pulse rounded bg-muted" />
            <div className="ml-auto h-5 w-14 animate-pulse rounded-full bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}
